import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import { useAuth } from "../context/AuthContext";
import authService from "../services/AuthService";

const LoginForm = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [step, setStep] = useState(1); // 1: login, 2: 2FA, 3: forgot password
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  // ----------------------------------------------------
  // MANEJADORES DE EVENTO
  // ----------------------------------------------------
  const handleLogin = async () => {
    // 1. Verificar si se han aceptado los Términos
    if (!termsAccepted) {
      toast.error("Debes aceptar los Términos y Condiciones.");
      return;
    }

    // 2. Verificar campos vacíos
    if (!email || !password) {
      toast.error("Los campos email y contraseña son obligatorios.");
      return;
    }

    // 3. Hacer la petición con el nuevo sistema de autenticación
    try {
      const result = await login(email, password);

      if (result.isSuccess) {
        toast.success(result.message || "Login exitoso");

        // Close the login form
        onClose();

        // Redirect based on user role
        if (result.user.role === "ciudadano-ignorado") {
          navigate("/dashboard");
        } else if (
          ["administrador", "tecnico-1", "tecnico-2"].includes(result.user.role)
        ) {
          navigate("/admin/dashboard");
        } else {
          navigate("/");
        }
      } else {
        // Handle 2FA if needed (future implementation)
        if (result.errorCode === "REQUIRES_2FA") {
          setStep(2);
        } else {
          toast.error(
            result.error || "El email o la contraseña no son correctos."
          );
        }
      }
    } catch (error) {
      toast.error("Ha ocurrido un error durante el login.");
    }
  };

  const handleVerify2FA = async () => {
    // Future implementation for 2FA
    try {
      // This would use the auth service for 2FA verification
      toast.info("Funcionalidad 2FA próximamente disponible");
    } catch (error) {
      toast.error("Ha ocurrido un error con la verificación 2FA.");
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error("Por favor, introduce tu email.");
      return;
    }

    try {
      const result = await authService.forgotPassword(forgotPasswordEmail);

      if (result.isSuccess) {
        toast.success(
          result.message ||
            "Se ha enviado un email con instrucciones para restablecer tu contraseña."
        );
        setStep(1); // Volver al formulario de login
        setForgotPasswordEmail("");
      } else {
        toast.error(
          result.error || "Error al enviar el email de recuperación."
        );
      }
    } catch (error) {
      toast.error("Ha ocurrido un error al procesar tu solicitud.");
    }
  };

  const handleShowForgotPassword = () => {
    setForgotPasswordEmail(email); // Pre-fill with current email if any
    setStep(3);
  };

  const handleBackToLogin = () => {
    setStep(1);
    setForgotPasswordEmail("");
  };

  // Botón Registrarme redirige a '/registro'
  const handleRegistro = () => {
    navigate("/registro");
  };

  return (
    <div className="login flex fondo-verde blanco fixed">
      <style>
        {`
          .login-form-custom input::placeholder {
            color: rgba(255, 255, 255, 0.7) !important;
          }
          .login-form-custom input::-webkit-input-placeholder {
            color: rgba(255, 255, 255, 0.7) !important;
          }
          .login-form-custom input::-moz-placeholder {
            color: rgba(255, 255, 255, 0.7) !important;
            opacity: 1;
          }
          .login-form-custom input:-ms-input-placeholder {
            color: rgba(255, 255, 255, 0.7) !important;
          }
        `}
      </style>
      {step === 1 ? (
        <div className="formulario-botones between gap-50 w-100 login-form-custom">
          <div className="formulario">
            <div className="campos flex gap-25">
              {/* CAMPO: EMAIL */}
              <div
                style={{ width: "250px" }}
                className="campo column email-field"
              >
                <label htmlFor="email">EMAIL</label>
                <input
                  type="text"
                  id="email"
                  style={{ width: "250px" }}
                  placeholder="ejemplo@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="fondo-verde blanco w100 text-white"
                  disabled={isLoading}
                />
              </div>

              {/* CAMPO: CONTRASEÑA (con icono ojo) */}
              <div className="contrasena">
                <div className="campo column">
                  <label htmlFor="password">CONTRASEÑA</label>
                  <div className="input-with-icon relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      placeholder="************"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="fondo-verde blanco w-200 text-white"
                      disabled={isLoading}
                    />
                    {/* Icono para visibilidad contraseña */}
                    <span
                      className="ojo absolute"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleShowForgotPassword}
                  className="contraseña-olvidada blanco"
                  style={{
                    background: "none",
                    border: "none",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  disabled={isLoading}
                >
                  ¿Has olvidado la contraseña?
                </button>
              </div>
            </div>
          </div>

          {/* TÉRMINOS y BOTONES */}
          <div className="botones-terminos column">
            <div className="terminos align-center">
              <input
                type="checkbox"
                id="accept-terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="fondo-blanco accent-green-950"
                disabled={isLoading}
              />
              <label htmlFor="accept-terms">
                He leído y acepto los{" "}
                <Link to="/terminos-condiciones" className="blanco">
                  Términos y Condiciones de uso
                </Link>
              </label>
            </div>
            <div className="botones between">
              <button
                className="iniciar fondo-blanco"
                onClick={handleLogin}
                disabled={isLoading}
              >
                {isLoading ? "Iniciando..." : "Iniciar sesión"}
              </button>
              <button
                className="cancelar blanco"
                onClick={onClose}
                type="button"
              >
                Cancelar
              </button>
              <Link to="/registro" className="blanco" onClick={handleRegistro}>
                Registrarme
              </Link>
            </div>
          </div>
        </div>
      ) : step === 2 ? (
        // FORMULARIO 2FA
        <div className="twofa-form column login-form-custom">
          <label htmlFor="twofa">CÓDIGO 2FA</label>
          <input
            type="text"
            id="twofa"
            placeholder="Introduce tu código"
            value={twoFACode}
            onChange={(e) => setTwoFACode(e.target.value)}
            disabled={isLoading}
          />
          <button onClick={handleVerify2FA} disabled={isLoading}>
            {isLoading ? "Verificando..." : "Verificar"}
          </button>
        </div>
      ) : (
        // FORMULARIO RECUPERAR CONTRASEÑA
        <div className="formulario-botones between gap-50 w-100 login-form-custom">
          <div className="formulario">
            <div className="campos flex gap-25">
              {/* CAMPO: EMAIL */}
              <div
                style={{ width: "350px" }}
                className="campo column email-field"
              >
                <label htmlFor="forgot-email">RECUPERAR CONTRASEÑA</label>
                <input
                  type="email"
                  id="forgot-email"
                  style={{ width: "350px" }}
                  placeholder="Introduce tu email para recuperar la contraseña"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="fondo-verde blanco w100"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* BOTONES */}
          <div className="botones-forgot column">
            <div className="botones between" style={{ gap: "10px" }}>
              <button
                className="iniciar fondo-blanco"
                onClick={handleForgotPassword}
                disabled={isLoading}
              >
                {isLoading ? "Enviando..." : "Enviar email"}
              </button>
              <button
                className="cancelar blanco ml-4"
                onClick={handleBackToLogin}
                type="button"
                disabled={isLoading}
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
